import { useState, useEffect, useCallback } from 'react'
import cx from 'classnames'
import { Tooltip } from 'antd'
import { RadioGroup } from 'components/RadioGroup/RadioGroup'
import { Radio } from 'components/RadioGroup/Radio'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { WorkspaceSamlSettings, WorkspaceSettings } from 'types'
import Styles from './SamlSettings.module.scss'
import SharedStyles from '../SecuritySettings.module.scss'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { SamlTextSetting } from './SamlTextSetting/SamlTextSetting'
import { reportError } from 'utils/error'
import { isNull } from 'lodash'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ReactComponent as InfoIcon } from 'assets/icons/info16.svg'
import { ActionableInput } from '../../components/ActionableInput/ActionableInput'
import { REACT_APP_API_URL } from 'env'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'

interface Props {
  workspace?: WorkspaceSettings
}

export const SamlSettings = ({ workspace }: Props) => {
  const [isSaving, setIsSaving] = useState(false)
  const [enableSaml, setEnableSaml] = useState(false)
  const [idpMetadataUrl, setIdpMetadataUrl] = useState('')
  const [idpMetadataXml, setIdpMetadataXml] = useState('')
  const [emailDomain, setEmailDomain] = useState('')
  const [useIdpMetadataUrl, setUseIdpMetadataUrl] = useState(true)
  const [origIdpMetadataUrl, setOrigIdpMetadataUrl] = useState('')
  const [origIdpMetadataXml, setOrigIdpMetadataXml] = useState('')
  const [origEmailDomain, setOrigEmailDomain] = useState('')
  const [emailDomainErrors, setEmailDomainErrors] = useState([])
  const [idpMetadataUrlErrors, setIdpMetadataUrlErrors] = useState([])
  const [idpMetadataXmlErrors, setIdpMetadataXmlErrors] = useState([])
  const singleSignOnUrl = workspace ? `${REACT_APP_API_URL.replace('/v1', '')}/saml/${workspace.id}/consume` : ''

  const workspaceKey = workspace?.key

  useEffect(() => {
    const loadSamlSettings = async (workspaceKey: string) => {
      const res = await WorkspaceClient.getSamlSettings(workspaceKey)
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        setEnableSaml(!!res.data.enabled)
        setUseIdpMetadataUrl(isNull(res.data.use_idp_metadata_url) ? true : res.data.use_idp_metadata_url)
        setIdpMetadataUrl(res.data.idp_metadata_url || '')
        setIdpMetadataXml(res.data.idp_metadata_xml || '')
        setEmailDomain(res.data.email_domain || '')
        setOrigIdpMetadataUrl(res.data.idp_metadata_url || '')
        setOrigIdpMetadataXml(res.data.idp_metadata_xml || '')
        setOrigEmailDomain(res.data.email_domain || '')
      }
    }
    if (workspaceKey) loadSamlSettings(workspaceKey)
  }, [workspaceKey])

  const handleSamlSettingsChange = useCallback(
    async (settings: WorkspaceSamlSettings, options: { alertSave: boolean } = { alertSave: true }) => {
      if (isSaving || !workspace) return

      setIsSaving(true)
      const res = await WorkspaceClient.saveSamlSettings(workspace.key, settings)

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        setEnableSaml(settings.enabled) // Toggle the checkbox only if we got a success response from the backend
        setOrigIdpMetadataUrl(idpMetadataUrl)
        setOrigIdpMetadataXml(idpMetadataXml)
        setOrigEmailDomain(emailDomain)
        if (options.alertSave) showToastMessage('Your SAML settings have been updated!', 'success')
      } else {
        showToastMessage("We couldn't save your SAML settings.", 'error')
        setEmailDomainErrors(res.data.errors[0]['email_domain'] || [])
        setIdpMetadataUrlErrors(res.data.errors[0]['idp_metadata_url'] || [])
        setIdpMetadataXmlErrors(res.data.errors[0]['idp_metadata_xml'] || [])
        const workspace_key = workspace.key
        reportError('Failed to update saml settings', {
          severity: 'error',
          metaData: {
            workspace_key,
            settings,
          },
        })
      }

      setIsSaving(false)
    },
    [idpMetadataUrl, idpMetadataXml, emailDomain, isSaving, workspace],
  )

  const copySingleSignOnUrl = useCallback(() => {
    navigator.clipboard.writeText(singleSignOnUrl)
    showToastMessage('Single sign-on URL copied to your clipboard', 'success')
  }, [singleSignOnUrl])

  return (
    <>
      <div className={SharedStyles.settingsOption}>
        <div className={SharedStyles.toggleRow}>
          <Heading level={5}>Enable SAML single sign-on (SSO)</Heading>
        </div>
        {!enableSaml && (
          <div className={Styles.reachOutText}>
            <Text variant="secondary" color="var(--dark-60)">
              Reach out to <a href="mailto:support@blaze.ai">support@blaze.ai</a> if you need to set up SAML for your
              organization.
            </Text>
          </div>
        )}
        <div className={SharedStyles.helpText}>
          <Text variant="secondary" color="var(--dark-60)">
            SAML SSO allows you to give your team members one account for all of the systems your business uses. Once
            enabled, you must have at least one active domain in order for SSO to be enforced.
          </Text>
        </div>
        {enableSaml && (
          <div className={Styles.samlSettings}>
            <SamlTextSetting
              isDisabled={!enableSaml}
              value={emailDomain}
              origValue={origEmailDomain}
              onChange={(e) => {
                setEmailDomain(e.target.value)
                setEmailDomainErrors([])
                setIdpMetadataUrlErrors([])
              }}
              errors={emailDomainErrors}
              label="Authorized Domains"
              hint="You can use comma separated values (e.g. some-domain.com, almanac.io)"
              id="saml-email-domain"
              onBlur={() =>
                handleSamlSettingsChange({
                  enabled: enableSaml,
                  use_idp_metadata_url: useIdpMetadataUrl,
                  idp_metadata_url: idpMetadataUrl,
                  idp_metadata_xml: idpMetadataXml,
                  email_domain: emailDomain,
                })
              }
            />

            <ActionableInput
              isDisabled={!enableSaml}
              label="Single sign-on URL"
              value={singleSignOnUrl}
              actions={[{ title: 'Copy', onClick: copySingleSignOnUrl }]}
            />

            <div className={cx(SharedStyles.settingsOption, { [SharedStyles.disabled]: !enableSaml })}>
              <div className={SharedStyles.inputRow}>
                <label className={SharedStyles.optionHeader} htmlFor="idp-metadata-radio-group">
                  <div className={Styles.idpMetadataLabel}>
                    IDP Metadata
                    <Tooltip
                      title={`Copy and paste the IDP metadata ${useIdpMetadataUrl ? 'URL' : 'XML'} below`}
                      placement="top"
                      trigger="hover"
                      overlayClassName={SharedStyles.antdOverride}
                    >
                      <InfoIcon className={SharedStyles.infoIcon} />
                    </Tooltip>
                  </div>

                  {enableSaml && (
                    <RadioGroup
                      id="idp-metadata-radio-group"
                      value={String(useIdpMetadataUrl)}
                      aria-label="IdP Metadata"
                      onChange={(value) => {
                        const useIt = value === 'true'
                        setUseIdpMetadataUrl(useIt)
                        handleSamlSettingsChange(
                          {
                            enabled: enableSaml,
                            use_idp_metadata_url: useIt,
                            idp_metadata_url: idpMetadataUrl,
                            idp_metadata_xml: idpMetadataXml,
                            email_domain: emailDomain,
                          },
                          { alertSave: false },
                        )
                      }}
                      className={Styles.idpMetadataRadioGroup}
                    >
                      <Radio value="true" aria-label="Use IdP Metadata URL" className={Styles.idpMetadataRadio}>
                        URL
                      </Radio>
                      <Radio
                        value="false"
                        aria-label="Use IdP Metadata XML"
                        className={Styles.idpMetadataRadio}
                        data-test-id="use-idp-metadata-xml"
                      >
                        XML
                      </Radio>
                    </RadioGroup>
                  )}
                </label>
              </div>
            </div>
            <SamlTextSetting
              isDisabled={!enableSaml}
              value={idpMetadataUrl}
              isHidden={!useIdpMetadataUrl}
              className={Styles.inRadio}
              origValue={origIdpMetadataUrl}
              onChange={(e) => {
                setIdpMetadataUrl(e.target.value.trim())
                setEmailDomainErrors([])
                setIdpMetadataUrlErrors([])
              }}
              errors={idpMetadataUrlErrors}
              label=""
              id="saml-idp-metadata-url"
              onBlur={() =>
                handleSamlSettingsChange({
                  enabled: enableSaml,
                  use_idp_metadata_url: useIdpMetadataUrl,
                  idp_metadata_url: idpMetadataUrl,
                  idp_metadata_xml: idpMetadataXml,
                  email_domain: emailDomain,
                })
              }
            />
            <SamlTextSetting
              isDisabled={!enableSaml}
              value={idpMetadataXml}
              isHidden={useIdpMetadataUrl}
              className={Styles.inRadio}
              origValue={origIdpMetadataXml}
              onChange={(e) => {
                setIdpMetadataXml(e.target.value.trim())
                setEmailDomainErrors([])
                setIdpMetadataXmlErrors([])
              }}
              errors={idpMetadataXmlErrors}
              label=""
              id="saml-idp-metadata-xml"
              type="textarea"
              onBlur={() =>
                handleSamlSettingsChange({
                  enabled: enableSaml,
                  use_idp_metadata_url: useIdpMetadataUrl,
                  idp_metadata_url: idpMetadataUrl,
                  idp_metadata_xml: idpMetadataXml,
                  email_domain: emailDomain,
                })
              }
            />
          </div>
        )}
      </div>
    </>
  )
}
